/**
 * Attendance locks — which of ERPat's two attendance locks, if either, shuts a
 * Team Attendance record for the signed-in approver.
 *
 * The server reports it as `approval.lock` on GET /v1/api/attendance/{id}
 * (HumanResource 1.57.0) and refuses every change to a locked record — approve,
 * reject, back to pending, correct the time logs — with 409 `attendance_locked`:
 *
 *   log_controls    the date is inside a locked payroll period (Log Controls);
 *   rolling_window  the record is older than the company's "Days to Lock
 *                   Attendance" window (ERPat's automatic attendance lock).
 *
 * An older backend sends no `lock` object — only `reason: 'attendance_locked'`,
 * and then only for Log Controls — so a locked verdict without a source reads as
 * the payroll-period lock.
 *
 * Kept apart from `models.ts` (which re-exports it) so `attendance-edit.ts` can
 * word its own lock note from the same place without importing `models.ts` back.
 */
import { asRecord, pick } from '@/services/api/http';

export type AttendanceLockSource = 'log_controls' | 'rolling_window' | '';

export interface AttendanceLockVM {
  /** Either lock applies: the record can't be approved, rejected, reopened or corrected. */
  locked: boolean;
  /** Which lock — '' when unlocked, or when the server didn't say. */
  source: AttendanceLockSource;
  /** The company's "Days to Lock Attendance" when `source` is the window; else null. */
  windowDays: number | null;
}

export const OPEN_ATTENDANCE_LOCK: AttendanceLockVM = { locked: false, source: '', windowDays: null };

function truthy(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  const s = String(value ?? '').toLowerCase().trim();
  return s === '1' || s === 'true' || s === 'yes';
}

/**
 * Read `approval.lock`. `reasonSaysLocked` covers a backend that predates the
 * object but already refused the record with `reason: 'attendance_locked'`.
 */
export function readAttendanceLock(raw: unknown, reasonSaysLocked = false): AttendanceLockVM {
  if (raw == null || typeof raw !== 'object') {
    return reasonSaysLocked ? { locked: true, source: '', windowDays: null } : OPEN_ATTENDANCE_LOCK;
  }

  const rec = asRecord(raw);
  if (!truthy(pick<unknown>(rec, ['locked', 'is_locked'], false)) && !reasonSaysLocked) {
    return OPEN_ATTENDANCE_LOCK;
  }

  const s = String(pick<unknown>(rec, ['source', 'lock_source'], '')).toLowerCase().trim();
  const source: AttendanceLockSource = s === 'log_controls' || s === 'rolling_window' ? s : '';
  const days = Number(pick<unknown>(rec, ['window_days'], NaN));

  return {
    locked: true,
    source,
    windowDays: source === 'rolling_window' && Number.isFinite(days) && days > 0 ? Math.floor(days) : null,
  };
}

/**
 * The opening clause of any sentence about a lock, worded for its source — e.g.
 * "Attendance older than 5 days is locked" — so the approval footer and the
 * correction note name the same lock the same way.
 */
export function attendanceLockLead(lock: AttendanceLockVM): string {
  if (lock.source === 'rolling_window') {
    return lock.windowDays != null
      ? `Attendance older than ${lock.windowDays} ${lock.windowDays === 1 ? 'day' : 'days'} is locked`
      : 'This attendance is past its lock period';
  }
  return 'This payroll period is locked';
}
